import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import type { TextCustomizations } from "src/types";

import { getState } from "@/features/helpers";
import {
  getIsFirstLoading,
  getIsLoading,
} from "@/features/requests/requests-reducer";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

const get = endpoints();

const initialState: TextCustomizations = {
  default_language: "en",
  custom_text: {
    sponsors: {
      gold: "Gold",
      silver: "Silver",
      bronze: "Bronze",
    },
  },
};

export const fetchTextCustomizations = createAsyncThunk<
  TextCustomizations,
  string
>("textCustomizations/fetch", async (slug: string, { rejectWithValue }) => {
  const response = await authenticatedFetch.fetchJSON<TextCustomizations>(
    get("TEXT_CUSTOMIZATIONS_API_V3")(slug)
  );

  if (isError(response)) {
    toast.error(response.description, { toastId: response.description });
    return rejectWithValue(response);
  }

  return response;
});

export const textCustomizationsAdapter = createEntityAdapter<TextCustomizations>();

const textCustomizationsSlice = createSlice({
  name: "textCustomizations",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTextCustomizations.fulfilled, (_state, action) => {
      return isError(action.payload) ? null : action.payload;
    });
  },
});

export const getTextCustomizations = createSelector(
  getState,
  (state) => state.textCustomizations
);

export const getIsTextCustomizationsLoading = createSelector(
  getState,
  getTextCustomizations,
  (state, textCustomizations) => {
    return (
      getIsLoading(state, fetchTextCustomizations) &&
      !textCustomizations.custom_text
    );
  }
);

export const getIsTextCustomizationsFirstLoading = createSelector(
  getState,
  (state) => {
    return getIsFirstLoading(state, fetchTextCustomizations);
  }
);

export default textCustomizationsSlice.reducer;
